import React, { useEffect, useMemo, useState } from 'react';
import Split from 'react-split';
import './Users.css';
import { FaPlus, FaEdit, FaTrash, FaRegSave, FaSearch, FaTimes } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;

async function fetchJSON(url, opts = {}) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
    ...(user?.userid ? { userid: user.userid } : {}),
  };
  // Include credentials so session cookies are sent even under subpaths/proxies
  const res = await fetch(url, { credentials: 'include', ...opts, headers });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const b = await res.json();
      if (b?.message) msg = b.message;
      if (b?.error) msg = b.error;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

const toDateTimeLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const fromDateTimeLocalInput = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const formatLockDisplay = (value) => {
  if (!value) return 'Not locked';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Locked (invalid date)';
  return date.toLocaleString();
};

const emptyUser = {
  userid: null,
  login: '',
  password: '',
  email: '',
  fullname: '',
  isclient: 0,
  clientid: -1,
  statusid: 1,
  notes: '',
  theme: 'light',
  sessiontimeout: 30,
  cell: '',
  sms: '',
  isadmin: 0,
  failed_attempts: 0,
  locked_until: null,
  // Notification preferences (UI only unless backend adds fields)
  notify_order_created: 0,
  notify_order_shipped: 0,
  notify_receipt_created: 0,
  notify_eorder_created: 0,
  notify_eorder_shipped: 0,
};

export default function Users() {
  const [splitSizes, setSplitSizes] = useState(() => {
    const stored = localStorage.getItem('users-split-sizes');
    return stored ? JSON.parse(stored) : [58, 42];
  });

  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showClientUsersAdmin, setShowClientUsersAdmin] = useState(true);

  // Searches
  const [qInternal, setQInternal] = useState(localStorage.getItem('usersQInternal') || '');
  const [qClient, setQClient] = useState(localStorage.getItem('usersQClient') || '');
  useEffect(() => localStorage.setItem('usersQInternal', qInternal), [qInternal]);
  useEffect(() => localStorage.setItem('usersQClient', qClient), [qClient]);

  // Independent toggles for showing inactive users
  const [showInactiveInternal, setShowInactiveInternal] = useState(() =>
    JSON.parse(localStorage.getItem('usersShowInactiveInternal') || 'false')
  );
  const [showInactiveClient, setShowInactiveClient] = useState(() =>
    JSON.parse(localStorage.getItem('usersShowInactiveClient') || 'false')
  );
  useEffect(() => {
    localStorage.setItem('usersShowInactiveInternal', JSON.stringify(showInactiveInternal));
  }, [showInactiveInternal]);
  useEffect(() => {
    localStorage.setItem('usersShowInactiveClient', JSON.stringify(showInactiveClient));
  }, [showInactiveClient]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';//meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Users ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [] );

  // Sorting
  const getInitialTopSort = () => {
    const s = localStorage.getItem('usersTopSort');
    return s ? JSON.parse(s) : { sortField: 'login', sortOrder: 'ASC' };
  };
  const getInitialBottomSort = () => {
    const s = localStorage.getItem('usersBottomSort');
    return s ? JSON.parse(s) : { sortField: 'login', sortOrder: 'ASC' };
  };
  const [topSortField, setTopSortField] = useState(getInitialTopSort().sortField);
  const [topSortOrder, setTopSortOrder] = useState(getInitialTopSort().sortOrder);
  const [bottomSortField, setBottomSortField] = useState(getInitialBottomSort().sortField);
  const [bottomSortOrder, setBottomSortOrder] = useState(getInitialBottomSort().sortOrder);

  useEffect(() => {
    localStorage.setItem('usersTopSort', JSON.stringify({ sortField: topSortField, sortOrder: topSortOrder }));
  }, [topSortField, topSortOrder]);
  useEffect(() => {
    localStorage.setItem('usersBottomSort', JSON.stringify({ sortField: bottomSortField, sortOrder: bottomSortOrder }));
  }, [bottomSortField, bottomSortOrder]);

  // Selection (row highlight only)
  const [selectedTopId, setSelectedTopId] = useState(null);
  const [selectedBottomId, setSelectedBottomId] = useState(null);

  // Editor state
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editorUser, setEditorUser] = useState(null);
  const [creatingType, setCreatingType] = useState('internal');
  const [saving, setSaving] = useState(false);
  // Editor-only helpers for client assignment UX
  const [clientAssignFilter, setClientAssignFilter] = useState('');
  useEffect(() => {
    // Defensive: clear any autofilled text when editor toggles
    if (editing || creating) setClientAssignFilter('');
  }, [editing, creating]);

  // Determine if editor is in a Client-user context (robust to data shape)
  const isClientContext = useMemo(() => {
    if (creating) return creatingType === 'client';
    const eu = editorUser || {};
    const byFlag = Number(eu.isclient) === 1;
    const byClientId = (eu.clientid ?? -1) !== -1;
    return !!(byFlag || byClientId);
  }, [creating, creatingType, editorUser]);
  const isInternalContext = !isClientContext;

  // Precompute filtered client list for the Assigned Clients checklist
  const filteredAssignClients = useMemo(() => {
    try {
      const list = Array.isArray(clients) ? clients : [];
      const term = String(clientAssignFilter || '').toLowerCase();
      if (!term) return list;
      return list.filter(c => String(c?.name || '').toLowerCase().includes(term));
    } catch { return Array.isArray(clients) ? clients : []; }
  }, [clients, clientAssignFilter]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Prefer lightweight clients list; fallback to full clients on error
      const [u, cMinimal, t] = await Promise.all([
        fetchJSON(`${API}/api/users`),
        // Prefer mobile alias to avoid internal-only gate on /api/clients for client-flagged admins
        fetchJSON(`${API}/api/mobile/clients/minimal`).catch(() => null),
        fetchJSON(`${API}/api/themes`)
      ]);
      setUsers(Array.isArray(u) ? u : []);
      let clientOpts = [];
      if (Array.isArray(cMinimal)) {
        clientOpts = cMinimal.map(x => ({ id: Number(x.id), name: String(x.name || `Client #${x.id}`) }));
      } else {
        // Fallback to full clients endpoint
        try {
          // Use mobile alias to bypass internal-only gate
          const cFull = await fetchJSON(`${API}/api/mobile/clients?limit=10000`);
          const list = Array.isArray(cFull?.data) ? cFull.data : (Array.isArray(cFull) ? cFull : []);
          clientOpts = list.map(x => ({ id: Number(x.id), name: String(x.name || `Client #${x.id}`) }));
        } catch (e) {
          clientOpts = [];
        }
      }
      setClients(clientOpts);
      setThemes(Array.isArray(t?.data) ? t.data : []);
    } catch (e) {
      console.error('Users load error:', e);
      setUsers([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    let ignore = false;
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API}/api/appconfig`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch config');
        const cfg = await res.json().catch(() => ({}));
        if (ignore) return;
        const interpret = (val) => {
          if (val == null) return true;
          if (typeof val === 'boolean') return val;
          if (typeof val === 'number') return Number(val) !== 0;
          const text = String(val).trim().toLowerCase();
          if (!text) return true;
          return text !== '0' && text !== 'false' && text !== 'no';
        };
        setShowClientUsersAdmin(interpret(cfg?.showclientusers_admin));
      } catch {
        if (!ignore) setShowClientUsersAdmin(true);
      }
    };
    fetchConfig();
    return () => { ignore = true; };
  }, []);

  const internalUsers = useMemo(() => users.filter(u => (u.clientid ?? -1) === -1), [users]);
  const clientUsers = useMemo(() => users.filter(u => (u.clientid ?? -1) !== -1), [users]);

  // Updated to treat statusid = 2 as disabled
  const isDisabled = (u) => {
    const v = u?.statusid;
    if (v === 0 || v === '0' || v === 2 || v === '2') return true;
    return false; // Treat undefined/null/anything else as active
  };

  const isLockedOut = (u) => {
    const until = u?.locked_until;
    if (!until) return false;
    const dt = new Date(until);
    if (Number.isNaN(dt.getTime())) return false;
    return dt > new Date();
  };

  const lockStatusText = (u) => {
    if (!u) return 'Not locked';
    if (isLockedOut(u)) return `Locked until ${formatLockDisplay(u.locked_until)}`;
    const attempts = Number(u.failed_attempts || 0);
    if (attempts > 0) return `${attempts} failed attempt${attempts === 1 ? '' : 's'}`;
    return 'Not locked';
  };

  const filterMatch = (u, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (u.login || '').toLowerCase().includes(s) ||
      (u.fullname || '').toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s)
    );
  };

  const sortRows = (rows, field, order) => {
    const dir = order === 'ASC' ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      const A = field === 'clientName' ? (a?.clientName ?? '') : a?.[field];
      const B = field === 'clientName' ? (b?.clientName ?? '') : b?.[field];
      const aKey = typeof A === 'number' ? A : String(A ?? '').toLowerCase();
      const bKey = typeof B === 'number' ? B : String(B ?? '').toLowerCase();
      if (aKey > bKey) return dir;
      if (aKey < bKey) return -dir;
      return 0;
    });
    return copy;
  };

  const topRows = useMemo(() => {
    const base = internalUsers
      .filter(u => (showInactiveInternal ? true : !isDisabled(u)))
      .filter(u => filterMatch(u, qInternal));
    return sortRows(base, topSortField, topSortOrder);
  }, [internalUsers, qInternal, showInactiveInternal, topSortField, topSortOrder]);

  const bottomRows = useMemo(() => {
    const base = clientUsers
      .filter(u => (showInactiveClient ? true : !isDisabled(u)))
      .filter(u => filterMatch(u, qClient));
    return sortRows(base, bottomSortField, bottomSortOrder);
  }, [clientUsers, qClient, showInactiveClient, bottomSortField, bottomSortOrder]);

  const handleTopSort = (field) => {
    if (topSortField === field) {
      setTopSortOrder(topSortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setTopSortField(field);
      setTopSortOrder('ASC');
    }
  };
  const handleBottomSort = (field) => {
    if (bottomSortField === field) {
      setBottomSortOrder(bottomSortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setBottomSortField(field);
      setBottomSortOrder('ASC');
    }
  };

  const onEdit = async (u) => {
    // Reset the Assigned Clients filter each time the editor opens to avoid stale text like "admin"
    setClientAssignFilter('');
    setEditorUser({ ...emptyUser, ...u, password: '', clientids: [] });
    setCreating(false);
    setEditing(true);
    try {
      // Safety: if clients list is empty (e.g., initial load failed), fetch a minimal list now
      if (!Array.isArray(clients) || clients.length === 0) {
        try {
          const cMin = await fetchJSON(`${API}/api/mobile/clients/minimal`);
          if (Array.isArray(cMin) && cMin.length) {
            setClients(cMin.map(x => ({ id: Number(x.id), name: String(x.name || `Client #${x.id}`) })));
          } else {
            // Fallback to full list if minimal not available
            try {
              const cFull = await fetchJSON(`${API}/api/mobile/clients?limit=10000`);
              const list = Array.isArray(cFull?.data) ? cFull.data : (Array.isArray(cFull) ? cFull : []);
              setClients(list.map(x => ({ id: Number(x.id), name: String(x.name || `Client #${x.id}`) })));
            } catch {}
          }
        } catch {}
      }
      const rows = await fetchJSON(`${API}/api/users/${u.userid}/clients`);
      const ids = Array.isArray(rows) ? rows.map(r => Number(r.id)).filter(n => Number.isFinite(n) && n > 0) : [];
      setEditorUser(s => ({ ...s, clientids: ids }));
    } catch {}
  };
  const onNewInternal = () => {
    setClientAssignFilter('');
    setCreating(true);
    setEditing(false);
    setCreatingType('internal');
    setEditorUser({ ...emptyUser, isclient: 0, clientid: -1, statusid: 1, theme: 'light', clientids: [] });
  };
  const onNewClient = () => {
    setClientAssignFilter('');
    setCreating(true);
    setEditing(false);
    setCreatingType('client');
    setEditorUser({
      ...emptyUser, isclient: 1,
      clientid: -1,
      statusid: 1, theme: 'light',
      clientids: []
    });
  };
  const onCancelEditor = () => {
    setClientAssignFilter('');
    setEditing(false);
    setCreating(false);
    setEditorUser(null);
  };

  const clearLockState = () => {
    setEditorUser(s => ({ ...s, locked_until: null, failed_attempts: 0 }));
  };

  const saveUser = async () => {
    if (!editorUser?.login) return alert('Login is required');
    if (creating && !editorUser.password) return alert('Password is required for new users');
    if (creatingType === 'client') {
      const ids = Array.isArray(editorUser?.clientids) ? editorUser.clientids.filter(n => Number.isFinite(n)) : [];
      if (!ids.length) return alert('Please assign at least one client to this user');
    }
    setSaving(true);
    try {
      if (creating) {
        const body = {
          ...editorUser,
          isclient: creatingType === 'client' ? 1 : 0,
          // Primary clientid = first assigned for client users; -1 for internal
          clientid: creatingType === 'client'
            ? Number((Array.isArray(editorUser.clientids) && editorUser.clientids[0]) || -1)
            : -1,
          clientids: Array.isArray(editorUser.clientids) ? editorUser.clientids : [],
        };
        const created = await fetchJSON(`${API}/api/users`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        setUsers(prev => [...prev, created]);
      } else {
        const id = editorUser.userid;
        const { userid, ...update } = editorUser;
        if (!update.password) delete update.password;
        update.clientids = Array.isArray(update.clientids) ? update.clientids : [];
        await fetchJSON(`${API}/api/users/${id}`, {
          method: 'PUT',
          body: JSON.stringify(update),
        });
        setUsers(prev => prev.map(u => (u.userid === id ? { ...u, ...update } : u)));
      }
      onCancelEditor();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.login}"?`)) return;
    try {
      await fetchJSON(`${API}/api/users/${u.userid}`, { method: 'DELETE' });
      setUsers(prev => prev.filter(x => x.userid !== u.userid));
      if (selectedTopId === u.userid) setSelectedTopId(null);
      if (selectedBottomId === u.userid) setSelectedBottomId(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const headerArrow = (active, order) => (active ? (order === 'ASC' ? ' ▲' : ' ▼') : '');

  const internalSection = (
    <div className="users-list-tablecol">
      <h2 className="section-title">Internal Users ({topRows.length})</h2>

      <div className="users-toolbar">
        <div className="search">
          <FaSearch />
          <input
            value={qInternal}
            onChange={(e) => setQInternal(e.target.value)}
            placeholder="Search internal users…"
            onKeyDown={(e) => { if (e.key === 'Escape') setQInternal(''); }}
          />
          {qInternal && (
            <button className="clear" title="Clear" onClick={() => setQInternal('')}>
              <FaTimes />
            </button>
          )}
        </div>

        <label className="inline-toggle">
          <input
            type="checkbox"
            checked={showInactiveInternal}
            onChange={(e) => setShowInactiveInternal(e.target.checked)}
          />
          Show Inactive Internal Users
        </label>

        <button className="btn primary" onClick={onNewInternal}>
          <FaPlus /> New Internal Login
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th onClick={() => handleTopSort('login')} className={topSortField === 'login' ? 'active-sort' : ''}>
                Login{headerArrow(topSortField === 'login', topSortOrder)}
              </th>
              <th onClick={() => handleTopSort('fullname')} className={topSortField === 'fullname' ? 'active-sort' : ''}>
                Full Name{headerArrow(topSortField === 'fullname', topSortOrder)}
              </th>
              <th onClick={() => handleTopSort('email')} className={topSortField === 'email' ? 'active-slot' : ''}>
                Email{headerArrow(topSortField === 'email', topSortOrder)}
              </th>
              <th onClick={() => handleTopSort('isadmin')} className={topSortField === 'isadmin' ? 'active-sort' : ''}>
                Admin{headerArrow(topSortField === 'isadmin', topSortOrder)}
              </th>
              <th onClick={() => handleTopSort('statusid')} className={topSortField === 'statusid' ? 'active-sort' : ''}>
                Status{headerArrow(topSortField === 'statusid', topSortOrder)}
              </th>
              <th>
                Lock Status
              </th>
              <th onClick={() => handleTopSort('theme')} className={topSortField === 'theme' ? 'active-sort' : ''}>
                Theme{headerArrow(topSortField === 'theme', topSortOrder)}
              </th>
            </tr>
          </thead>
          <tbody>
            {topRows.length ? topRows.map(u => {
              const disabled = isDisabled(u);
              const locked = isLockedOut(u);
              return (
                <tr
                  key={u.userid}
                  className={`${selectedTopId === u.userid ? 'active' : ''} ${disabled ? 'disabled-row' : ''} ${locked ? 'locked-row' : ''}`}
                  onClick={() => setSelectedTopId(u.userid)}
                  style={{ cursor: 'pointer' }}
                  title="Click to select"
                >
                  <td className="td-actions">
                    <div className="actions">
                      <button className="mini-btn" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(u); }}>
                        <FaEdit size={16} />
                      </button>
                      <button className="mini-btn danger" title="Delete" onClick={(e) => { e.stopPropagation(); deleteUser(u); }}>
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                  <td>{u.login}</td>
                  <td>{u.fullname || ''}</td>
                  <td>{u.email || ''}</td>
                  <td>{u.isadmin ? 'Yes' : 'No'}</td>
                  <td>{disabled ? 'Disabled' : 'Active'}</td>
                  <td>{lockStatusText(u)}</td>
                  <td>{u.theme || ''}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={8}>No internal users</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  const clientSection = (
    <div className="users-list-tablecol">
      <h2 className="section-title">Client Users ({bottomRows.length})</h2>

      <div className="users-toolbar">
        <div className="search">
          <FaSearch />
          <input
            value={qClient}
            onChange={(e) => setQClient(e.target.value)}
            placeholder="Search client users…"
            onKeyDown={(e) => { if (e.key === 'Escape') setQClient(''); }}
          />
          {qClient && (
            <button className="clear" title="Clear" onClick={() => setQClient('')}>
              <FaTimes />
            </button>
          )}
        </div>

        <label className="inline-toggle">
          <input
            type="checkbox"
            checked={showInactiveClient}
            onChange={(e) => setShowInactiveClient(e.target.checked)}
          />
          Show Inactive Client Users
        </label>

        <button className="btn" onClick={onNewClient}>
          <FaPlus /> New Client Login
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th onClick={() => handleBottomSort('login')} className={bottomSortField === 'login' ? 'active-sort' : ''}>
                Login{headerArrow(bottomSortField === 'login', bottomSortOrder)}
              </th>
              <th onClick={() => handleBottomSort('fullname')} className={bottomSortField === 'fullname' ? 'active-sort' : ''}>
                Full Name{headerArrow(bottomSortField === 'fullname', bottomSortOrder)}
              </th>
              <th onClick={() => handleBottomSort('email')} className={bottomSortField === 'email' ? 'active-sort' : ''}>
                Email{headerArrow(bottomSortField === 'email', bottomSortOrder)}
              </th>
              <th onClick={() => handleBottomSort('clientName')} className={bottomSortField === 'clientName' ? 'active-sort' : ''}>
                Client Name{headerArrow(bottomSortField === 'clientName', bottomSortOrder)}
              </th>
              <th onClick={() => handleBottomSort('statusid')} className={bottomSortField === 'statusid' ? 'active-sort' : ''}>
                Status{headerArrow(bottomSortField === 'statusid', bottomSortOrder)}
              </th>
              <th>
                Lock Status
              </th>
              <th onClick={() => handleBottomSort('theme')} className={bottomSortField === 'theme' ? 'active-sort' : ''}>
                Theme{headerArrow(bottomSortField === 'theme', bottomSortOrder)}
              </th>
            </tr>
          </thead>
          <tbody>
            {bottomRows.length ? bottomRows.map(u => {
              const disabled = isDisabled(u);
              const locked = isLockedOut(u);
              return (
                <tr
                  key={u.userid}
                  className={`${selectedBottomId === u.userid ? 'active' : ''} ${disabled ? 'disabled-row' : ''} ${locked ? 'locked-row' : ''}`}
                  onClick={() => setSelectedBottomId(u.userid)}
                  style={{ cursor: 'pointer' }}
                  title="Click to select"
                >
                  <td className="td-actions">
                    <div className="actions">
                      <button className="mini-btn" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(u); }}>
                        <FaEdit size={16} />
                      </button>
                      <button className="mini-btn danger" title="Delete" onClick={(e) => { e.stopPropagation(); deleteUser(u); }}>
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                  <td>{u.login}</td>
                  <td>{u.fullname || ''}</td>
                  <td>{u.email || ''}</td>
                  <td>{u.clientName || ''}</td>
                  <td>{disabled ? 'Disabled' : 'Active'}</td>
                  <td>{lockStatusText(u)}</td>
                  <td>{u.theme || ''}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={8}>No client users</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className={`users-wrapper-col`}>
      <div className="users-left-pane">
        {showClientUsersAdmin ? (
          <Split
            direction="vertical"
            sizes={splitSizes}
            minSize={[200, 200]}
            gutterSize={6}
            onDragEnd={(sizes) => {
              setSplitSizes(sizes);
              localStorage.setItem('users-split-sizes', JSON.stringify(sizes));
            }}
            className="users-split"
            style={{ height: 'calc(100vh - 140px)', minHeight: 0 }}
          >
            {internalSection}
            {clientSection}
          </Split>
        ) : (
          internalSection
        )}
      </div>

      {(editing || creating) && editorUser && (
        <div className="users-modal-overlay" onClick={onCancelEditor}>
          <div className="users-modal" onClick={(e)=>e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="userModalTitle">
            <div className="modal-head">
              <h3 id="userModalTitle">
                {creating
                  ? (creatingType === 'client' ? 'New Client User' : 'New Internal User')
                  : (isClientContext ? `Edit Client User: ${editorUser.login}` : `Edit Internal User: ${editorUser.login}`)
                }
                <span
                  className={`type-chip ${isClientContext ? 'client' : 'internal'}`}
                  title={isClientContext ? 'Client User' : 'Internal User'}
                >
                  {isClientContext ? 'Client' : 'Internal'}
                </span>
              </h3>
              <button className="icon-btn" onClick={onCancelEditor} aria-label="Close"><FaTimes /></button>
            </div>

            <div className="modal-body form-scroll">
              <div className="form-grid">
                {creating && creatingType !== 'client' && (
                  <label>
                    User Type
                    <select
                      value={creatingType}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCreatingType(v);
                        setEditorUser(s => ({
                          ...s,
                          isclient: v === 'client' ? 1 : 0,
                          clientid: v === 'client' ? -1 : -1,
                          clientids: v === 'client' ? [] : []
                        }));
                      }}
                    >
                      <option value="internal">Internal</option>
                      <option value="client">Client</option>
                    </select>
                  </label>
                )}

                {/* For new client users, remove the primary Client dropdown.
                    Associated clients are selected via the Assigned Clients checklist below. */}
                {!creating && isClientContext && (
                  <label className="span-2">
                    Client
                    <input value={(clients.find(c => c.id === Number(editorUser.clientid))?.name) || ''} readOnly />
                  </label>
                )}

                <label>
                  Login
                  <input
                    value={editorUser.login}
                    onChange={e => setEditorUser(s => ({ ...s, login: e.target.value }))}
                  />
                </label>

                {/* Move Full Name directly after Login for clearer data entry flow */}
                <label>
                  Full Name
                  <input
                    value={editorUser.fullname || ''}
                    onChange={e => setEditorUser(s => ({ ...s, fullname: e.target.value }))}
                  />
                </label>

                {/* Multi-client assignments — visible for client users (creating or editing) */}
                {isClientContext && (
                  <label className="span-2">
                    Assigned Clients
                    <div className="multiselect">
                      {/* mini toolbar: filter + bulk actions */}
                      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                        <input
                          value={clientAssignFilter}
                          onChange={e => { if (e.isTrusted !== false) setClientAssignFilter(e.target.value); }}
                          onFocus={() => setClientAssignFilter('')}
                          placeholder="Filter clients…"
                          style={{ flex: 1 }}
                          name="client-filter"
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="none"
                          spellCheck={false}
                          data-1p-ignore
                          data-lpignore="true"
                          inputMode="search"
                        />
                        <button
                          type="button"
                          className="btn"
                          onClick={() => {
                            const ids = (clients || [])
                              .filter(c => !clientAssignFilter || String(c.name || '').toLowerCase().includes(clientAssignFilter.toLowerCase()))
                              .map(c => c.id);
                            setEditorUser(s => ({ ...s, clientids: Array.from(new Set([...(s.clientids || []), ...ids])) }));
                          }}
                          title="Select all filtered"
                        >Select All</button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => {
                            if (!Array.isArray(editorUser?.clientids) || editorUser.clientids.length === 0) return;
                            const toRemove = new Set(
                              (clients || [])
                                .filter(c => !clientAssignFilter || String(c.name || '').toLowerCase().includes(clientAssignFilter.toLowerCase()))
                                .map(c => c.id)
                            );
                            setEditorUser(s => ({
                              ...s,
                              clientids: (Array.isArray(s.clientids) ? s.clientids.filter(id => !toRemove.has(id)) : [])
                            }));
                          }}
                          title="Clear all filtered"
                        >Clear</button>
                      </div>
                      <div className="options" style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #444', borderRadius: 4, padding: 8 }}>
                        <div className="assign-list">
                          {clients.length === 0 && (
                            <div style={{ color: '#aaa' }}>No clients available</div>
                          )}
                          {(() => {
                            const filtered = (clients || [])
                              .filter(c => !clientAssignFilter || String(c.name || '').toLowerCase().includes(clientAssignFilter.toLowerCase()));
                            if (!filtered.length) {
                              return (
                                <div style={{ color: '#aaa' }}>No matching clients{clientAssignFilter ? ` for "${clientAssignFilter}"` : ''}</div>
                              );
                            }
                            return filtered.map(c => {
                              const checked = Array.isArray(editorUser?.clientids) && editorUser.clientids.includes(c.id);
                            const id = `assign-client-${c.id}`;
                            return (
                              <div key={c.id} className="assign-item">
                                <input
                                  id={id}
                                  type="checkbox"
                                  checked={!!checked}
                                  onChange={e => {
                                    setEditorUser(s => {
                                      const cur = Array.isArray(s.clientids) ? s.clientids.slice() : [];
                                      if (e.target.checked) {
                                        if (!cur.includes(c.id)) cur.push(c.id);
                                      } else {
                                        const i = cur.indexOf(c.id);
                                        if (i !== -1) cur.splice(i, 1);
                                      }
                                      return { ...s, clientids: cur };
                                    });
                                  }}
                                />
                                <label htmlFor={id}>{c.name}</label>
                              </div>
                            );
                              });
                              })()
                              }
                        </div>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, opacity: .8 }}>
                        Select all companies this client login should access.
                      </div>
                    </div>
                  </label>
                )}

                <label>
                  <span>Password</span>
                  <input
                    type="password"
                    value={editorUser.password}
                    onChange={e => setEditorUser(s => ({ ...s, password: e.target.value }))}
                    placeholder={creating ? undefined : 'Leave blank to keep'}
                  />
                  {!creating && (
                    <div className="field-hint">(leave blank to keep)</div>
                  )}
                </label>


                <label>
                  Email
                  <input
                    type="email"
                    value={editorUser.email || ''}
                    onChange={e => setEditorUser(s => ({ ...s, email: e.target.value }))}
                  />
                </label>

                <label>
                  Status
                  <select
                    value={editorUser.statusid ?? 1}
                    onChange={e => setEditorUser(s => ({ ...s, statusid: Number(e.target.value) }))}
                  >
                    <option value={1}>Active</option>
                    <option value={2}>Disabled</option>
                  </select>
                </label>

                {isInternalContext && (
                  <label>
                    Admin
                    <select
                      value={editorUser.isadmin ?? 0}
                      onChange={e => setEditorUser(s => ({ ...s, isadmin: Number(e.target.value) }))}
                    >
                      <option value={0}>No</option>
                      <option value={1}>Yes</option>
                    </select>
                  </label>
                )}

                {!creating && (
                  <div className="span-2 lockout-section">
                    <div className="lockout-row">
                      <div>
                        <div className="lockout-label">Current lock</div>
                        <div className="lockout-value">{formatLockDisplay(editorUser.locked_until)}</div>
                        <div className="lockout-meta">
                          {Number(editorUser.failed_attempts || 0)} failed attempt{Number(editorUser.failed_attempts || 0) === 1 ? '' : 's'}
                        </div>
                      </div>
                      <label className="lockout-input">
                        <div className="lockout-input-row">
                          <span>Set Locked Until</span>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setEditorUser(s => ({ ...s, locked_until: null }))}
                          >
                            Clear
                          </button>
                        </div>
                        <input
                          type="datetime-local"
                          value={toDateTimeLocalInput(editorUser.locked_until)}
                          onChange={e => {
                            const raw = e.target.value;
                            setEditorUser(s => ({ ...s, locked_until: raw ? fromDateTimeLocalInput(raw) : null }));
                          }}
                        />
                        <div className="field-hint">Leave blank to keep unlocked</div>
                      </label>
                    </div>
                    <div className="lockout-actions">
                      <button type="button" className="btn" onClick={clearLockState}>
                        Clear Lock / Reset Attempts
                      </button>
                      <div className="lockout-meta">Changes apply when you save.</div>
                    </div>
                  </div>
                )}

                <label>
                  Theme
                  <select
                    value={editorUser.theme || 'light'}
                    onChange={e => setEditorUser(s => ({ ...s, theme: e.target.value }))}
                  >
                    {themes.map(t => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </label>

                <label>
                  Session Timeout (mins)
                  <input
                    type="number"
                    min={1}
                    value={editorUser.sessiontimeout ?? 30}
                    onChange={e => setEditorUser(s => ({ ...s, sessiontimeout: Number(e.target.value) }))}
                  />
                </label>

                <label>
                  Cell
                  <input
                    value={editorUser.cell || ''}
                    onChange={e => setEditorUser(s => ({ ...s, cell: e.target.value }))}
                  />
                </label>

                <label>
                  SMS
                  <input
                    value={editorUser.sms || ''}
                    onChange={e => setEditorUser(s => ({ ...s, sms: e.target.value }))}
                  />
                </label>

                {/* Notification preferences — client users only */}
                {isClientContext && (
                  <div className="span-2">
                    <div className="fieldset-group">
                      <div className="checkbox-grouping">
                        <label className="group-label">Order Notifications</label>
                        <div className="checkbox-list">
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={Boolean(editorUser.notify_order_created)}
                              onChange={e => setEditorUser(s => ({ ...s, notify_order_created: e.target.checked ? 1 : 0 }))}
                            />
                            <span>Send email for every order created</span>
                          </label>
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={Boolean(editorUser.notify_order_shipped)}
                              onChange={e => setEditorUser(s => ({ ...s, notify_order_shipped: e.target.checked ? 1 : 0 }))}
                            />
                            <span>Send email for every order shipped</span>
                          </label>
                        </div>
                      </div>

                      <div className="checkbox-grouping">
                        <label className="group-label">eOrder Notifications</label>
                        <div className="checkbox-list">
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={Boolean(editorUser.notify_eorder_created)}
                              onChange={e => setEditorUser(s => ({ ...s, notify_eorder_created: e.target.checked ? 1 : 0 }))}
                            />
                            <span>Send email for every eOrder created</span>
                          </label>
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={Boolean(editorUser.notify_eorder_shipped)}
                              onChange={e => setEditorUser(s => ({ ...s, notify_eorder_shipped: e.target.checked ? 1 : 0 }))}
                            />
                            <span>Send email for every eOrder shipped</span>
                          </label>
                        </div>
                      </div>

                      <div className="checkbox-grouping">
                        <label className="group-label">Receipt Notifications</label>
                        <div className="checkbox-list">
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={Boolean(editorUser.notify_receipt_created)}
                              onChange={e => setEditorUser(s => ({ ...s, notify_receipt_created: e.target.checked ? 1 : 0 }))}
                            />
                            <span>Send email for receipt created</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <label className="span-2">
                  Notes
                  <textarea
                    value={editorUser.notes || ''}
                    onChange={e => setEditorUser(s => ({ ...s, notes: e.target.value }))}
                  />
                </label>
              </div>
            </div>

            <div className="modal-foot actions">
              <button className="btn" onClick={onCancelEditor}>Cancel</button>
              <button className="btn primary" onClick={saveUser} disabled={saving}>
                <FaRegSave size={16} /> {saving ? 'Saving…' : 'Save User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
